import { Fragment, useState, useRef, useEffect } from 'react'

import { MdKeyboardArrowDown } from 'react-icons/md'
import { useTheme } from 'styled-components'

import { Checkbox } from '@components/Checkbox'

import {
  ActionsheetBox,
  DropdownContainer,
  DropdownItem,
  MultiselectOptions,
  MultiselectPlaceholder,
  MultiselectText,
  StyledBadge,
  StyledPressable,
  StyledScrollView,
} from './styles'

interface IItem {
  description: string
  id: number
}

interface IProps {
  items: IItem[]
  onChange?: (values: number[]) => void
  borderColor: string
  borderWidth: number
  defaultValues?: (number | undefined)[] | undefined
  isDisabled?: boolean
  placeHolder?: string
}

export function Multiselect({
  items,
  onChange,
  borderColor,
  borderWidth,
  defaultValues = [],
  isDisabled = false,
  placeHolder,
}: IProps) {
  const [isOpen, setIsOpen] = useState(false)
  const theme = useTheme()
  const containerRef = useRef<HTMLDivElement>(null)

  const values = items
    .filter((permit) => defaultValues.includes(permit.id))
    .map((item) => item)

  const [selectedItems, setSelectedItems] = useState<IItem[]>(values)

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (
        containerRef.current &&
        !containerRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  function handlePress(selectedItem: IItem) {
    let newList = []
    if (hasObject(selectedItem)) {
      newList = selectedItems.filter(
        (oldItem) => oldItem.id !== selectedItem.id,
      )
    } else {
      newList = [...selectedItems, selectedItem]
    }
    onChange && onChange(newList.map((item) => item.id))
    setSelectedItems(newList)
  }

  function hasObject(obj: IItem) {
    const hasObject = !!selectedItems.find((item) => item.id === obj.id)?.id
    return hasObject
  }

  return (
    <div ref={containerRef} style={{ position: 'relative', width: '100%' }}>
      <StyledPressable
        onClick={() => !isDisabled && setIsOpen(!isOpen)}
        disabled={isDisabled}
        $borderColor={borderColor}
        $borderWidth={borderWidth}
        $isDisabled={isDisabled}
      >
        <StyledBadge>
          {selectedItems.length > 0 ? (
            <MultiselectText $isDisabled={isDisabled}>
              {selectedItems.map((item) => item.description).join(', ')}
            </MultiselectText>
          ) : (
            <MultiselectPlaceholder>{placeHolder}</MultiselectPlaceholder>
          )}
        </StyledBadge>
        {!isDisabled && (
          <MdKeyboardArrowDown size={24} color={theme.colors.textDisabled} />
        )}
      </StyledPressable>
      {isOpen && (
        <DropdownContainer>
          <StyledScrollView>
            {items.map((item) => (
              <Fragment key={item.id}>
                <DropdownItem onClick={() => handlePress(item)}>
                  <ActionsheetBox>
                    <Checkbox isChecked={hasObject(item)} />
                    <MultiselectOptions>{item.description}</MultiselectOptions>
                  </ActionsheetBox>
                </DropdownItem>
              </Fragment>
            ))}
          </StyledScrollView>
        </DropdownContainer>
      )}
    </div>
  )
}
